import { PAYROLL_CONCEPT_KIND, PAYROLL_CONCEPT_SLUG, PayrollConceptKind } from './payroll-concept.model';
import { PayrollLine } from './payroll-line.model';
import { Payroll } from './payroll.model';

export function getPayrollLines(payroll: Pick<Payroll, 'payroll_lines'> | { payroll_lines?: PayrollLine[] | null }): PayrollLine[] {
  return payroll.payroll_lines ?? [];
}

export function findPayrollLineBySlug(lines: PayrollLine[] | null | undefined, slug: string): PayrollLine | undefined {
  return (lines ?? []).find(line => line.lineable?.slug === slug);
}

/** Amount for a concept slug from payroll lines. */
export function payrollLineAmountBySlug(lines: PayrollLine[] | null | undefined, slug: string, fallback: number = 0): number {
  const line = findPayrollLineBySlug(lines, slug);
  if (line != null) {
    return Number(line.amount ?? 0);
  }

  return Number(fallback || 0);
}

export function payrollLineMetaNumber(lines: PayrollLine[] | null | undefined, slug: string, key: string, fallback: number = 0): number {
  const meta = findPayrollLineBySlug(lines, slug)?.meta;
  if (meta == null || meta[key] == null) {
    return fallback;
  }

  return Number(meta[key]);
}

export function payrollLineMetaBoolean(
  lines: PayrollLine[] | null | undefined,
  slug: string,
  key: string,
  fallback: boolean = false
): boolean {
  const meta = findPayrollLineBySlug(lines, slug)?.meta;
  if (meta == null || meta[key] == null) {
    return fallback;
  }

  return Boolean(meta[key]);
}

export function payrollLinesOfKind(lines: PayrollLine[] | null | undefined, kind: PayrollConceptKind): PayrollLine[] {
  return (lines ?? []).filter(line => line.lineable?.kind === kind);
}

export function payrollLinesAmountOfKind(lines: PayrollLine[] | null | undefined, kind: PayrollConceptKind): number {
  return payrollLinesOfKind(lines, kind).reduce((sum, line) => sum + Number(line.amount ?? 0), 0);
}

export function bisualPlusPayrollLines(lines: PayrollLine[] | null | undefined): PayrollLine[] {
  return (lines ?? []).filter(
    line =>
      line.lineable != null &&
      !line.lineable.is_system &&
      (line.lineable.kind == null || line.lineable.kind === PAYROLL_CONCEPT_KIND.SALARY)
  );
}

export function bisualPlusesTotal(lines: PayrollLine[] | null | undefined): number {
  return bisualPlusPayrollLines(lines).reduce((sum, line) => sum + Number(line.amount ?? 0), 0);
}

export function estimateExtraHoursAmount(additional_hours: number, brute_base_salary: number): number {
  if (additional_hours <= 0 || brute_base_salary <= 0) {
    return 0;
  }

  return Math.round(additional_hours * ((brute_base_salary * 10) / 1800) * 100) / 100;
}

export function payrollConceptKindEmoji(kind: PayrollConceptKind | null | undefined): string {
  switch (kind) {
    case PAYROLL_CONCEPT_KIND.BENEFIT:
      return '🎁';
    case PAYROLL_CONCEPT_KIND.EXPENSE:
      return '🧾';
    case PAYROLL_CONCEPT_KIND.DEDUCTION:
      return '➖';
    case PAYROLL_CONCEPT_KIND.SALARY:
    default:
      return '🎖️';
  }
}

/** Chips for benefit / deduction / extra expense lines not already rendered by form-specific UI. */
export function extraPayrollConceptLineChips(
  lines: PayrollLine[] | null | undefined,
  excludeSlugs: string[] = []
): Array<{ key: string; emoji: string; label: string; amount: number; kind: PayrollConceptKind }> {
  const excluded = new Set(excludeSlugs);
  const kinds: PayrollConceptKind[] = [PAYROLL_CONCEPT_KIND.BENEFIT, PAYROLL_CONCEPT_KIND.DEDUCTION, PAYROLL_CONCEPT_KIND.EXPENSE];

  const chips: Array<{ key: string; emoji: string; label: string; amount: number; kind: PayrollConceptKind }> = [];

  for (const kind of kinds) {
    for (const line of payrollLinesOfKind(lines, kind)) {
      const slug = line.lineable?.slug;
      if (slug == null || excluded.has(slug)) {
        continue;
      }

      const amount = Number(line.amount ?? 0);
      if (amount === 0) {
        continue;
      }

      chips.push({
        key: slug,
        emoji: payrollConceptKindEmoji(kind),
        label: line.lineable?.name ?? slug,
        amount,
        kind,
      });
    }
  }

  return chips;
}

export { PAYROLL_CONCEPT_KIND, PAYROLL_CONCEPT_SLUG };
